import { fillProfileUrl } from '@utils/urlUtils';
import { PublicCertification } from '@wix/ambassador-academy-certifications-v1-account-certification/build/cjs/types.impl';
import { Box, Button, Divider, Heading, Image, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { UploadExport } from '@wix/wix-ui-icons-common';
import clsx from 'clsx';
import { FC, Suspense, useMemo } from 'react';
import { useProfileContext } from '../../Contexts/ProfileContext';
import Certifications from '../Certifications';
import { shouldRenderCertifications } from '../Certifications/CertificationsUtils';
import ProviderReviewSummary from '../ProviderReviewSummary';
import Services from '../Services/Services';
import s from './ContactPanel.module.scss';

type Props = {
  isMobileView: boolean;
  onContactClicked: () => void;
  copy?: (value: string) => Promise<void>;
  onCopyClicked: () => void;
  shareUrl: string;
  certifications: PublicCertification[];
};

const ContactPanel: FC<Props> = ({
  isMobileView,
  onContactClicked,
  copy,
  onCopyClicked = () => {},
  shareUrl,
  certifications,
}) => {
  const { partnerProfile, isMyProfile, enabled, isRenderedInModal } =
    useProfileContext();
  const { t } = useTranslation();

  const hasServices = useMemo(
    () => (partnerProfile.services?.values?.length || 0) > 0,
    [partnerProfile.services],
  );

  const hasCertifications = useMemo(
    () => shouldRenderCertifications(certifications),
    [certifications],
  );

  return (
    <Box
      minWidth={isMobileView ? '100%' : '302px'}
      direction="vertical"
      dataHook="profile-contact-panel"
    >
      <div
        className={clsx(
          !isMobileView && s.fixedLeftbar,
          s.container,
          isMobileView && s.mobile,
        )}
      >
        <Box
          direction="vertical"
          className={clsx(
            s.contentContainer,
            s.sectionPadding,
            isMobileView && s.mobile,
          )}
        >
          <div className={s.profileImageContainer}>
            <Image
              alt={partnerProfile?.professionalInformation?.businessName || ''}
              borderRadius="100px"
              transparent
              width="90px"
              height="90px"
              src={fillProfileUrl(
                partnerProfile?.professionalInformation?.profileImage || '',
                180,
                180,
              )}
            />
          </div>
          <Box
            className={clsx(s.businessNameContainer, isMobileView && s.mobile)}
          >
            <Heading
              size="large"
              as="h2"
              weight="normal"
              ellipsis
              maxLines={2}
              dataHook="contact-panel-business-name"
            >
              {partnerProfile.professionalInformation?.businessName}
            </Heading>
          </Box>

          {isMobileView && (
            <Box width="100%">
              <ProviderReviewSummary
                isStickyMobileView={false}
                isMobileView={isMobileView}
              />
            </Box>
          )}

          <Box
            paddingTop="6px"
            width="100%"
            className={clsx(s.tagline, isMobileView && s.mobile)}
          >
            <Text
              size={isMobileView ? 'medium' : 'small'}
              weight={'thin'}
              ellipsis
              maxLines={2}
              secondary={isMobileView}
              width="100%"
            >
              {partnerProfile.professionalInformation?.tagline}
            </Text>
          </Box>

          {!isMobileView && (
            <Box paddingTop="24px" width="100%">
              <ProviderReviewSummary
                isStickyMobileView={false}
                isMobileView={isMobileView}
              />
            </Box>
          )}

          <Box
            paddingTop={isMobileView ? '18px' : '24px'}
            paddingBottom={isMobileView ? '30px' : '0'}
            gap={isMobileView ? '12px' : '6px'}
          >
            {!isMyProfile && enabled && !isRenderedInModal && (
              <Button
                className={s.contactButton}
                dataHook="profile-contact-button"
                onClick={onContactClicked}
                size="large"
                skin="dark"
              >
                {t('marketplace-contact-button')}
              </Button>
            )}
            <Button
              className={s.shareButton}
              dataHook="profile-contact-panel-share"
              priority="secondary"
              skin="dark"
              size="medium"
              onClick={() => {
                copy?.(shareUrl);
                onCopyClicked();
              }}
            >
              <UploadExport />
            </Button>
          </Box>
        </Box>

        {!isMobileView && (
          <>
            {hasServices && <Divider className={s.divider} />}

            <Box className={clsx(s.sectionPadding, isMobileView && s.mobile)}>
              <Services />
            </Box>

            {hasCertifications && <Divider className={s.divider} />}

            <Box className={clsx(s.sectionPadding, isMobileView && s.mobile)}>
              <Suspense fallback="">
                <Certifications
                  isMobileView={isMobileView}
                  variant="compact"
                  data={certifications}
                />
              </Suspense>
            </Box>
          </>
        )}
      </div>
    </Box>
  );
};

export default ContactPanel;
